// src/api/mesas.js
import { apiFetch } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';

const convertirDesdeAPI = (apiMesa) => {
  if (!apiMesa) return null;
  let source = apiMesa;
  if (apiMesa.data && !Array.isArray(apiMesa.data) && typeof apiMesa.data === 'object') source = apiMesa.data;
  else if (apiMesa.mesa && typeof apiMesa.mesa === 'object') source = apiMesa.mesa;

  const getProp = (obj, propName) => {
    if (!obj) return undefined;
    const keys = Object.keys(obj);
    const normalizedProp = propName.toLowerCase().replace(/_/g, '');
    const foundKey = keys.find(k => k.toLowerCase().replace(/_/g, '') === normalizedProp);
    return foundKey ? obj[foundKey] : undefined;
  };

  const id = source.idMesa || source.IdMesa || getProp(source, 'idMesa') || source.id;

  return {
    idMesa: id, id,
    idZonaMesa: source.idZonaMesa ?? getProp(source, 'idZonaMesa') ?? null,
    nombreZona: toTitleCase(source.nombreZona || source.NombreZona || '') || null,
    numero: source.numero || source.Numero || getProp(source, 'numero') || '',
    capacidad: source.capacidad ?? source.Capacidad ?? 4,
    estado: (source.estado || source.Estado || 'libre').toLowerCase(),
    qr_codigo: source.qr_codigo || source.qrCodigo || source.QrCodigo || null,
    observaciones: source.observaciones || source.Observaciones || '',
    fecha_creacion: source.fecha_creacion || source.fechaCreacion || source.FechaCreacion || null,
    // Relaciones
    TotalPedidos: source.TotalPedidos ?? source.totalPedidos ?? 0,
    PedidosActivos: source.PedidosActivos ?? source.pedidosActivos ?? 0,
    TotalReservaciones: source.TotalReservaciones ?? source.totalReservaciones ?? 0,
    ...source, idMesa: id,
  };
};

const convertirParaAPI = (frontendMesa) => ({
  idZonaMesa: frontendMesa.idZonaMesa ?? null,
  numero: (frontendMesa.numero || '').trim(),
  capacidad: Number(frontendMesa.capacidad) || 4,
  estado: frontendMesa.estado || 'libre',
  qr_codigo: frontendMesa.qr_codigo || null,
  observaciones: frontendMesa.observaciones || null,
});

const convertirListaDesdeAPI = (apiList) => {
  let lista = apiList;
  if (apiList && apiList.data && Array.isArray(apiList.data)) lista = apiList.data;
  else if (apiList && apiList.mesas && Array.isArray(apiList.mesas)) lista = apiList.mesas;
  if (!Array.isArray(lista)) return [];
  return lista.map(convertirDesdeAPI);
};

export const formatearFechaMesa = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  try {
    const fecha = new Date(fechaISO);
    const opciones = { year: 'numeric', month: 'short', day: 'numeric' };
    if (completo) { opciones.hour = '2-digit'; opciones.minute = '2-digit'; }
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch { return fechaISO; }
};

export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  const mapa = {
    libre: 'Libre',
    ocupada: 'Ocupada',
    reservada: 'Reservada',
    inactivo: 'Inactivo',
  };
  return mapa[estado.toLowerCase()] || (estado.charAt(0).toUpperCase() + estado.slice(1));
};

export const colorEstadoMesa = (estado) => {
  const mapa = {
    libre: 'emerald',
    ocupada: 'rose',
    reservada: 'amber',
    inactivo: 'gray',
  };
  return mapa[estado?.toLowerCase()] || 'gray';
};

export const obtenerEstadisticasMesas = (mesas) => {
  const total = mesas.length;
  const libres = mesas.filter(m => m.estado === 'libre').length;
  const ocupadas = mesas.filter(m => m.estado === 'ocupada').length;
  const reservadas = mesas.filter(m => m.estado === 'reservada').length;
  const inactivas = mesas.filter(m => m.estado === 'inactivo').length;
  return {
    total, libres, ocupadas, reservadas, inactivas,
    porcentajeOcupacion: total > 0 ? Math.round((ocupadas / total) * 100) : 0,
    capacidadTotal: mesas.reduce((acc, m) => acc + (m.capacidad || 0), 0),
  };
};

export const buscarMesas = (mesas, termino) => {
  if (!termino || termino.trim() === '') return mesas;
  const t = termino.toLowerCase();
  return mesas.filter(m =>
    m.numero?.toLowerCase().includes(t) ||
    m.nombreZona?.toLowerCase().includes(t) ||
    m.observaciones?.toLowerCase().includes(t)
  );
};

export const filtrarMesasPorEstado = (mesas, estado) => {
  if (estado === 'all') return mesas;
  return mesas.filter(m => m.estado === estado);
};

export const filtrarMesasPorZona = (mesas, idZona) => {
  if (!idZona || idZona === 'all') return mesas;
  return mesas.filter(m => m.idZonaMesa === Number(idZona));
};

// ============== API calls ==============

export const obtenerMesas = async () => {
  try {
    const response = await apiFetch('/Mesa');
    const mesas = convertirListaDesdeAPI(response);
    return { success: true, data: mesas, count: mesas.length };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

export const obtenerMesasLibres = async () => {
  try {
    const response = await apiFetch('/Mesa/libres');
    const mesas = convertirListaDesdeAPI(response);
    return { success: true, data: mesas };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

export const obtenerMesasPorZona = async (idZonaMesa) => {
  try {
    if (!idZonaMesa) throw new Error('ID de zona requerido');
    const response = await apiFetch(`/Mesa/zona/${idZonaMesa}`);
    return { success: true, data: convertirListaDesdeAPI(response) };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

export const obtenerMesasPorEstado = async (estado) => {
  try {
    if (!estado) throw new Error('Estado requerido');
    const response = await apiFetch(`/Mesa/estado/${estado}`);
    return { success: true, data: convertirListaDesdeAPI(response) };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

export const obtenerMesaPorId = async (id) => {
  try {
    const response = await apiFetch(`/Mesa/${id}`);
    return { success: true, data: convertirDesdeAPI(response) };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerMesaConRelaciones = async (id) => {
  try {
    const response = await apiFetch(`/Mesa/${id}/conRelaciones`);
    return { success: true, data: convertirDesdeAPI(response) };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const buscarMesasPorCriterios = async (criterios = {}) => {
  try {
    const response = await apiFetch('/Mesa/search', {
      method: 'POST',
      body: JSON.stringify({
        idZonaMesa: criterios.idZonaMesa || null,
        numero: criterios.numero || null,
        estado: criterios.estado || null,
        capacidadMinima: criterios.capacidadMinima || null,
      }),
    });
    return { success: true, data: convertirListaDesdeAPI(response) };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

export const verificarMesaPorId = async (id) => {
  try {
    const response = await apiFetch(`/Mesa/${id}/exists`);
    return { success: true, existe: response.existe ?? response.exists ?? false };
  } catch (error) {
    return { success: false, error: error.message, existe: false };
  }
};

export const verificarNumeroMesa = async (numero, excludeId = null) => {
  try {
    const qs = excludeId ? `?excludeId=${excludeId}` : '';
    const response = await apiFetch(`/Mesa/numero/${encodeURIComponent(numero)}/exists${qs}`);
    return { success: true, existe: response.existe ?? response.exists ?? false };
  } catch (error) {
    return { success: false, error: error.message, existe: false };
  }
};

export const crearMesa = async (mesaData) => {
  try {
    if (!mesaData.numero || !mesaData.numero.trim()) throw new Error('El número es requerido');
    const response = await apiFetch('/Mesa', {
      method: 'POST',
      body: JSON.stringify(convertirParaAPI(mesaData)),
    });
    return { success: true, data: convertirDesdeAPI(response), message: 'Mesa creada' };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const actualizarMesa = async (id, mesaData) => {
  try {
    if (!id) throw new Error('ID requerido');
    if (!mesaData.numero || !mesaData.numero.trim()) throw new Error('El número es requerido');
    const response = await apiFetch(`/Mesa/${id}`, {
      method: 'PUT',
      body: JSON.stringify(convertirParaAPI(mesaData)),
    });
    return { success: true, data: convertirDesdeAPI(response), message: 'Mesa actualizada' };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const cambiarEstadoMesa = async (id, nuevoEstado) => {
  try {
    if (!id) throw new Error('ID requerido');
    if (!nuevoEstado) throw new Error('Estado requerido');
    const response = await apiFetch(`/Mesa/${id}/estado`, {
      method: 'PUT',
      body: JSON.stringify({ estado: nuevoEstado }),
    });
    return { success: true, data: convertirDesdeAPI(response) };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const eliminarMesa = async (id) => {
  try {
    if (!id) throw new Error('ID requerido');
    const response = await apiFetch(`/Mesa/${id}`, { method: 'DELETE' });
    return { success: true, data: response, message: response?.mensaje || 'Mesa desactivada' };
  } catch (error) {
    let errorMsg = 'Error al desactivar la mesa';
    if (error.response?.data?.mensaje) errorMsg = error.response.data.mensaje;
    else if (error.response?.data?.error) errorMsg = error.response.data.error;
    else if (error.message) errorMsg = error.message;
    return { success: false, error: errorMsg, data: null };
  }
};

export const obtenerEstadisticasMesasAPI = async () => {
  try {
    const response = await apiFetch('/Mesa/estadisticas');
    return { success: true, data: response };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export default {
  obtenerMesas, obtenerMesasLibres, obtenerMesasPorZona, obtenerMesasPorEstado,
  obtenerMesaPorId, obtenerMesaConRelaciones, buscarMesasPorCriterios,
  verificarMesaPorId, verificarNumeroMesa,
  crearMesa, actualizarMesa, cambiarEstadoMesa, eliminarMesa,
  obtenerEstadisticasMesasAPI,
  formatearFechaMesa, convertirEstadoATexto, colorEstadoMesa,
  obtenerEstadisticasMesas, buscarMesas, filtrarMesasPorEstado, filtrarMesasPorZona,
  convertirDesdeAPI, convertirParaAPI,
};